import * as React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Image from 'next/image';
import styles from './Menu.module.css';
import { useMapDisplayContext } from '../MapDisplayContext';
import { useSelector } from 'react-redux';
import { ReduxState } from 'ducks';
import { AnimatedClickableDiv } from '../AnimatedClickableDiv';
import { LanguageSelect } from './LanguageSelect';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { CustomMenuItem } from 'models/digitalMap';
import { useDigitalMapCustomMenuItems } from '../useDigitalMapCustomMenuItems';

interface MenuItemProps {
  iconUrl: string;
  label: string;
  onClick: () => void;
}

interface SubmenuItemProps {
  iconUrl: string;
  label: string;
  submenuItems: CustomMenuItem[];
}

interface Props {
  onClose: () => void;
}

export const Menu = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const { setActivePage, setShowStampRallyModal, resetDisplay } = useMapDisplayContext();

  const digitalMap = useSelector(selectDigitalMap);
  const stampRally = useSelector(
    (state: ReduxState) => state.universal.digitalGuidanceStampRally.stampRally
  );

  const customMenuItems = useDigitalMapCustomMenuItems();

  const MenuItem = ({ iconUrl, label, onClick }: MenuItemProps) => {
    return (
      <AnimatedClickableDiv
        className={clsx(styles['menu-item'])}
        clickedClassName={styles['clicked']}
        onClick={onClick}
      >
        <div className={styles['menu-item-content']}>
          {iconUrl && (
            <Image
              width={16}
              height={24}
              priority
              src={iconUrl}
              alt={label}
              className={styles['menu-icon']}
            />
          )}
          <div className={styles['menu-label']}>{label}</div>
        </div>
        <Image width={7} height={24} priority src="/static/images/ic_arrow_right.svg" alt="" />
      </AnimatedClickableDiv>
    );
  };

  const SubmenuItem = ({ iconUrl, label, submenuItems }: SubmenuItemProps) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
      <>
        <AnimatedClickableDiv
          className={clsx(styles['menu-item'])}
          clickedClassName={styles['clicked']}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className={styles['menu-item-content']}>
            {iconUrl && (
              <Image
                width={16}
                height={24}
                priority
                src={iconUrl}
                alt={label}
                className={styles['menu-icon']}
              />
            )}
            <div className={styles['menu-label']}>{label}</div>
          </div>
          <Image
            style={{
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
            width={7}
            height={24}
            priority
            src="/static/images/ic_arrow_right.svg"
            alt=""
          />
        </AnimatedClickableDiv>
        {isOpen && (
          <div className={styles['submenu-items']}>
            {submenuItems.map((item, index) => (
              <AnimatedClickableDiv
                key={index}
                className={clsx(styles['menu-item'])}
                clickedClassName={styles['clicked']}
                onClick={() => {
                  window.open(item.url, '_blank');
                  onClose();
                }}
              >
                <div className={styles['menu-item-content']} style={{ paddingLeft: 40 }}>
                  <div className={styles['menu-label']}>{item.label}</div>
                </div>
              </AnimatedClickableDiv>
            ))}
          </div>
        )}
      </>
    );
  };

  const getMenuItem = (item: CustomMenuItem) => {
    switch (item.item_type) {
      case 'MAP':
        return (
          <>
            <MenuItem
              label={t('Map')}
              iconUrl="/static/images/map/map_pin.svg"
              onClick={() => {
                resetDisplay();
                setActivePage('MAP');
                onClose();
              }}
            />
            <hr className={styles['separator']} />
          </>
        );
      case 'LIST':
        return (
          <>
            <MenuItem
              label={t('List', {
                context: 'map-menu',
              })}
              iconUrl="/static/images/map/list.svg"
              onClick={() => {
                resetDisplay();
                setActivePage('LIST');
                onClose();
              }}
            />
            <hr className={styles['separator']} />
          </>
        );
      case 'MY_ACCOUNT':
        return (
          <>
            <MenuItem
              label={t('My Account')}
              iconUrl="/static/images/map/user_circle.svg"
              onClick={() => {
                resetDisplay();
                setActivePage('ACCOUNT');
                onClose();
              }}
            />
            <hr className={styles['separator']} />
          </>
        );
      case 'TICKETS':
        return (
          <>
            <MenuItem
              label={t('Tickets')}
              iconUrl="/static/images/map/ticket.svg"
              onClick={() => {
                resetDisplay();
                setActivePage('TICKETS');
                onClose();
              }}
            />
            <hr className={styles['separator']} />
          </>
        );
      case 'SCHEDULE':
        return (
          <>
            <MenuItem
              label={t('Schedule')}
              iconUrl="/static/images/map/schedule.svg"
              onClick={() => {
                resetDisplay();
                setActivePage('SCHEDULE');
                onClose();
              }}
            />
            <hr className={styles['separator']} />
          </>
        );
      case 'STAMP_RALLY':
        return (
          stampRally && (
            <>
              <MenuItem
                label={t('Stamp Rally')}
                iconUrl="/static/images/map/stamp_rally.svg"
                onClick={() => {
                  resetDisplay();
                  setShowStampRallyModal(true);
                  onClose();
                }}
              />
              <hr className={styles['separator']} />
            </>
          )
        );
      case 'ITINERARIES':
        return (
          <>
            <MenuItem
              label={t('Itineraries')}
              iconUrl="/static/images/map/calendar_star.svg"
              onClick={() => {
                resetDisplay();
                setActivePage('ITINERARIES.LIST');
                onClose();
              }}
            />
            <hr className={styles['separator']} />
          </>
        );
      case 'CUSTOM_SUBMENU':
        return (
          <>
            <SubmenuItem
              label={item.label}
              iconUrl="/static/images/map/layers.svg"
              submenuItems={item.sub_menu_items}
            />
            <hr className={styles['separator']} />
          </>
        );
      case 'CUSTOM_LINK':
        return (
          <>
            <MenuItem
              label={item.label}
              iconUrl=""
              onClick={() => {
                window.open(item.url, '_blank');
                onClose();
              }}
            />
            <hr className={styles['separator']} />
          </>
        );
    }
  };

  return (
    <aside className={styles['menu']}>
      {customMenuItems.map((item) => getMenuItem(item))}

      {(digitalMap?.display_settings?.language_menu_items?.length ?? 0) > 0 && (
        <div className={styles['language-select-container']}>
          <LanguageSelect key={digitalMap?.seo_params?.language} />
        </div>
      )}
    </aside>
  );
};
